import React from 'react';
import styled from 'styled-components';
import { css } from '/imports/ui/css';

export interface ProjectManagementMetric {
  /** Accent dot colour — use projectManagementMetricDot(index) unless a metric owns a meaningful colour. */
  dot: string;
  label: string;
  /** A figure that rides on the label row, for a tile whose value area is given over to its own layout. */
  headValue?: React.ReactNode;
  value: React.ReactNode;
  sub?: React.ReactNode;
  /** Extra class on this tile, for a caller that lays its own figures out inside one. */
  className?: string;
}

// Two of these are not in the palette; they come from the design prototype. Consumers cycle through them
// rather than inventing a fifth, so every banner in the app draws from the same four accents.
const dots = ['#5FBBFF', '#71F2FF', css.secondaryColor, css.teal1];

/**
 * The accent for the nth tile, wrapping past the fourth.
 * @param index - the tile's position in the banner
 * @returns a dot colour
 */
export const projectManagementMetricDot = (index: number) => dots[index % dots.length];

const ProjectManagementMetricsBannerStyled = styled.div`
  background: linear-gradient(90deg, ${css.primaryColor} 0%, ${css.secondaryColor} 100%);
  border-radius: ${css.inputBorderRadius};
  padding: 1.25rem 1.375rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12.5rem, 1fr));
  gap: 1.125rem 1.5rem;
  .metric {
    min-width: 0;
    /* Each tile is a size container so the figure below can scale to the tile it landed in. */
    container-type: inline-size;
    padding: 1rem 1.125rem;
    background: linear-gradient(160deg, rgba(0, 0, 0, 0.24) 0%, rgba(0, 0, 0, 0.06) 100%);
  }
  .metric-head { display: flex; align-items: baseline; gap: 0.5rem; margin-bottom: 0.5rem; }
  .metric-head .dot { align-self: center; }
  /* Follows the label immediately rather than sitting at the far end of the row, so the pair reads as one
     phrase. flex: none keeps the label the part that ellipsises when the tile is narrow, never the figure.
     Smaller than .metric-value — it shares a line with a label — but well clear of the figures below it,
     which is the ranking it is up there to keep. */
  .metric-head-value { flex: none; font-size: 1.25rem; line-height: 1; font-weight: 600; color: ${css.white}; }
  .dot {
    display: inline-block; width: 0.5rem; height: 0.5rem; border-radius: 50%; flex: 0 0 0.5rem;
    box-shadow: rgba(255, 255, 255, 0.16) 0 0 0 3px;
  }
  .metric-label {
    font-size: 0.6875rem; letter-spacing: 0.5px; text-transform: uppercase;
    color: rgba(255, 255, 255, 0.85); font-weight: 600;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  /* A figure shrinks with its tile (full size from ~270px of tile, down to 20px in the narrowest) and wraps
     at a SPACE when it still does not fit — the Solar · BESS tile carries two figures and used to run out
     of the box. word-break stays normal so a number never splits mid-digits ("$88,097,01 / 8"); the callers
     join a figure to its unit with a non-breaking space so "790.01 kW" travels as one word. break-word is
     the last resort for a single figure too long for any line: wrapped reads better than spilled. */
  .metric-value { font-size: clamp(1.25rem, 11cqw, 1.875rem); line-height: 1.2; font-weight: 600; color: ${css.white}; overflow-wrap: break-word; }
  .metric-sub { font-size: 0.8125rem; color: rgba(255, 255, 255, 0.78); margin-top: 0.25rem; overflow-wrap: break-word; }
  /* The finance banners' sub-line carries an ACTUAL — a figure in its own right, so half again the size. */
  &.large-sub .metric-sub { font-size: clamp(0.9375rem, 7cqw, 1.21875rem); }
`;

interface ProjectManagementMetricsBannerProps {
  metrics: ProjectManagementMetric[];
  className?: string;
}

/** Headline roll-up: a gradient band of metric tiles, one per figure worth leading with. */
export const ProjectManagementMetricsBanner = (props: ProjectManagementMetricsBannerProps) => (
  <ProjectManagementMetricsBannerStyled className={`metrics-banner${props.className ? ` ${props.className}` : ''}`}>
    {
      props.metrics.map((m) => (
        <div className={`metric${m.className ? ` ${m.className}` : ''}`} key={m.label}>
          <div className="metric-head">
            <span className="dot" style={{ background: m.dot }} />
            <span className="metric-label">{m.label}</span>
            {m.headValue != null && <span className="metric-head-value">{m.headValue}</span>}
          </div>
          <div className="metric-value">{m.value}</div>
          {m.sub != null && <div className="metric-sub">{m.sub}</div>}
        </div>
      ))
    }
  </ProjectManagementMetricsBannerStyled>
);
